/**
 * AdminLayout — sidebar navigation shell wrapping all 6 admin routes
 * (iter114s.76).
 *
 * Replaces the in-page tab switcher with a left-rail nav so admins
 * jump between work-states without breadcrumb-clicking through a
 * single landing page. The legacy `/admin/classic` route is gone in
 * this iteration — every tab from the old dashboard has been split
 * into a dedicated route under this shell.
 */
import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutGrid, ListOrdered, FileCheck2, Factory, FileQuestion, Users, Cog,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const NAV_ITEMS = [
  { to: '/admin/action-queue', label: 'Action Queue',  Icon: LayoutGrid,    testId: 'admin-nav-action-queue' },
  { to: '/admin/orders',       label: 'Orders',        Icon: ListOrdered,   testId: 'admin-nav-orders' },
  { to: '/admin/proofing',     label: 'Proofing',      Icon: FileCheck2,    testId: 'admin-nav-proofing' },
  { to: '/admin/production',   label: 'Production',    Icon: Factory,       testId: 'admin-nav-production' },
  { to: '/admin/quotes',       label: 'Quotes',        Icon: FileQuestion,  testId: 'admin-nav-quotes' },
  { to: '/admin/customers',    label: 'Customers',     Icon: Users,         testId: 'admin-nav-customers' },
  { to: '/admin/system',       label: 'System',        Icon: Cog,           testId: 'admin-nav-system' },
];

export const AdminLayout = ({ children, fullBleed = false }) => {
  const { user, isAdmin, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && (!user || !isAdmin)) navigate('/');
  }, [isLoading, user, isAdmin, navigate]);

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#F4FBF6] via-white to-[#E8F1E6] flex"
      data-testid="admin-layout"
    >
      {/* Left rail */}
      <aside className="sticky top-0 h-screen w-56 flex-shrink-0 bg-[#14281D] text-white shadow-lg flex flex-col">
        <div className="px-5 py-5 border-b border-white/10">
          <Link to="/admin/action-queue" className="flex items-center gap-2 group">
            <div className="w-9 h-9 rounded-lg bg-[#6FBF73] text-[#14281D] grid place-items-center font-black shadow">⚡</div>
            <div className="leading-tight">
              <p className="text-sm font-bold tracking-tight group-hover:text-[#A8D8AC] transition">EG3 Admin</p>
              <p className="text-[10px] text-white/60 uppercase tracking-wider">Operations Cockpit</p>
            </div>
          </Link>
        </div>
        <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.map(({ to, label, Icon, testId }) => {
            const active = location.pathname.startsWith(to);
            return (
              <Link
                key={to}
                to={to}
                data-testid={testId}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition ${
                  active
                    ? 'bg-[#6FBF73] text-[#14281D] font-bold shadow'
                    : 'text-white/85 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span>{label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="px-3 py-3 border-t border-white/10 text-[10px] text-white/50 space-y-1">
          <p>{user?.email}</p>
          <Link to="/splash" className="text-[#A8D8AC] hover:text-white transition" data-testid="admin-nav-back-to-store">
            ← Back to store
          </Link>
        </div>
      </aside>

      {/* Main canvas */}
      <main className={`flex-1 min-w-0 ${fullBleed ? '' : 'px-6 py-8'}`}>
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
